/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { UtrvStatus } from '../../constants/status';
import { generateUrl } from '../../routes/util';
import { CustomInitiatives, CustomReportProps, CustomReportType, NewInitiativesOrSurvey } from '../../types/custom-report';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { FormGenerator, FieldProps } from '@g17eco/molecules/form';
import { CustomReportRoutes } from './CustomReportRoutes';
import { SubsidiarySelectionTable } from './partials/SubsidiarySelectionTable';
import { SurveySelectionTable, TableNodeWithSurveyIds } from './partials/SurveySelectionTable';
import './styles.scss';
import { convertToUtrvStatuses, FormData, getInitialState } from './util/downloadReportHandler';
import { getSelectedSurveyNodeIds } from './util/treeNavigation';
import { QUESTION, SURVEY } from '@constants/terminology';
import { useCreateCustomReportMutation, useUpdateCustomReportMutation } from '@api/custom-reports';
import { BasicAlert } from '@g17eco/molecules/alert';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { DownloadUtrvStatusCombined } from '../../types/common';

const THROTTLE_DELAY = 500;

const fields: FieldProps[] = [
  {
    code: 'name',
    type: 'text',
    label: 'Name of custom report',
    placeholder: `For example: Gender diversity report for CSR ${(new Date()).getFullYear()}`,
    required: true,
  },
  {
    code: 'description',
    type: 'textarea',
    label: 'Description',
    placeholder: '',
  },
  {
    code: 'status',
    type: 'radio',
    label: 'Data I want to download:',
    placeholder: '',
    options: [
      {
        value: DownloadUtrvStatusCombined.All,
        label: `All ${QUESTION.PLURAL} (including unanswered)`,
      },
      {
        value: DownloadUtrvStatusCombined.AllAnswered,
        label: `All answered ${QUESTION.PLURAL} (answered and verified)`,
      },
      {
        value: UtrvStatus.Verified,
        label: `Verified ${QUESTION.PLURAL} only (excludes assured)`,
      },
      {
        value: DownloadUtrvStatusCombined.Assured,
        label: `Assured ${QUESTION.PLURAL} (includes restated)`,
      },
    ]
  },
];

const getInitialSurveyState = (customReport?: CustomInitiatives): Partial<NewInitiativesOrSurvey['survey']> | undefined => {
  return customReport?.survey;
};

const isFormValid = (formData?: Partial<FormData>, surveyData?: Partial<NewInitiativesOrSurvey['survey']>) => {
  if (!formData?.name || surveyData?.initiativeIds?.length === 0 || surveyData?.surveyFilters?.length === 0) {
    return false;
  }
  return true;
}

const CustomReportEditFormSubsidiary = ({ customReport, initiativeId }: CustomReportProps<CustomInitiatives>) => {
  const [formData, setFormData] = useState<Partial<FormData> | undefined>(getInitialState(customReport));
  const [surveyData, setSurveyData] = useState<Partial<NewInitiativesOrSurvey['survey']> | undefined>(getInitialSurveyState(customReport));
  const throttleTimeout = React.useRef<ReturnType<typeof setTimeout> | undefined>();
  const [isSurveyListDirty, setSurveyListDirty] = useState(false);

  const history = useHistory();

  const [createCustomReport, { isLoading: isCreating }] = useCreateCustomReportMutation();
  const [updateCustomReport, { isLoading: isUpdating }] = useUpdateCustomReportMutation();
  const isSaving = isCreating || isUpdating;

  const setInitiativeIds = useCallback((initiativeIds: string[]) => {
    setSurveyData(state => ({
      ...state,
      initiativeIds
    }));
  }, [setSurveyData]);

  React.useEffect(() => setFormData(getInitialState(customReport)), [customReport]);

  const handleSelectedNode = useCallback((selectedNodes?: TableNodeWithSurveyIds[]) => {
    setSurveyData((state) => ({
      ...state,
      surveyFilters:
        selectedNodes?.map(({ surveyType: type, period, effectiveDate }) => ({ type, period, effectiveDate })) ?? [],
    }));
  }, []);

  const handleUpdateInitiativeIds = (ids: string[]) => {
    setSurveyListDirty(true);

    if (throttleTimeout.current) {
      clearTimeout(throttleTimeout.current);
    }

    throttleTimeout.current = setTimeout(() => {
      setInitiativeIds(ids);
      setSurveyListDirty(false);
    }, THROTTLE_DELAY);
  }

  const handleUpdate = (e: React.ChangeEvent<{ name: string, value: string | number | undefined }>) => {
    const { name, value } = e.target;

    if (!fields.map(f => f.code).includes(name)) {
      return;
    }

    setFormData({
      ...(formData ?? {}),
      [name]: value
    });
  };

  const isEditMode = Boolean(formData?._id);

  const handleCancel = () => {
    const url = generateUrl(CustomReportRoutes.ROOT, { initiativeId });
    history.push(url);
  }

  const handleSubmit = async () => {
    if (!formData || !surveyData) {
      return;
    }

    if (
      !formData.name ||
      !surveyData.initiativeIds ||
      surveyData.initiativeIds.length === 0 ||
      !surveyData.surveyFilters ||
      surveyData.surveyFilters.length === 0
    ) {
      return;
    }

    const data: NewInitiativesOrSurvey = {
      type: CustomReportType.Initiatives,
      name: formData.name,
      description: formData.description,
      survey: {
        initiativeIds: surveyData.initiativeIds,
        surveyFilters: surveyData.surveyFilters,
        ...convertToUtrvStatuses(formData.status),
      },
    };

    const method = formData._id ? updateCustomReport({initiativeId, reportId: formData._id, formData: data}) : createCustomReport({initiativeId, formData: data});
    method
      .then(() => {
        const url = generateUrl(CustomReportRoutes.ROOT, { initiativeId });
        history.push(url);
        setFormData(undefined);
        setSurveyData(undefined);
      });
  }

  return <>
    <FormGenerator
      form={formData ?? {}}
      fields={fields.map(f => ({ ...f, disabled: isSaving }))}
      updateForm={handleUpdate}
    />

    <div className='mt-3'>
      <hr />

      <div className='mt-4 strong'>Which subsidiaries would you like to compare:</div>

      <div className='mt-3 downloads-table-container'>
        <SubsidiarySelectionTable initiativeId={initiativeId} selectedIds={customReport?.survey.initiativeIds} handleSetSelectedIds={handleUpdateInitiativeIds} />
      </div>
    </div>

    <div className='mt-5'>
      <hr />

      <div className='mt-4 strong'>Which {SURVEY.SINGULAR}(s) would you like to compare:</div>

      <div className='mt-3 downloads-table-container'>
        <LoadingPlaceholder height={400} isLoading={isSurveyListDirty}>
          {surveyData?.initiativeIds && surveyData?.initiativeIds.length > 0
            ?
            <SurveySelectionTable
              isMulti
              initiativeId={initiativeId}
              initiativeIds={surveyData?.initiativeIds ?? []}
              handleSelectedNodes={handleSelectedNode}
              selectedNodeIds={getSelectedSurveyNodeIds(customReport?.survey ? customReport.survey.surveyFilters : [])}
            />
            :
            <BasicAlert type={'warning'}>Please select subsidiaries above</BasicAlert>
          }
        </LoadingPlaceholder>
      </div>
    </div>
    <div className='mt-4 text-right'>
      <Button color='link-secondary' onClick={handleCancel} className='mr-4'>Cancel</Button>
      <SubmitButton
        disabled={!isFormValid(formData, surveyData) || isSaving}
        color='primary'
        onClick={handleSubmit}>
        {isEditMode ? 'Update' : 'Create'}
      </SubmitButton>
    </div>
  </>
}

export default CustomReportEditFormSubsidiary;
